import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { logError, logInfo, logWarn } from '@/utils/logger';

// Some blockers let the script request hang indefinitely instead of firing
// `onerror`, which otherwise reads identically to "still loading" forever.
// Well past normal load time for a same-origin script this size — past it,
// `scriptLoadStalled()` reports the hang without giving up the load itself
// (`scriptLoaded`/`scriptLoadFailed` can still land after this fires, they
// just won't have a stall warning to retract).
const SCRIPT_STALL_WATCHDOG_MS = 5000;

export type ForterTokenSource = 'service' | 'cookie' | 'none';

export interface ForterFunnelSnapshot {
  isInitialized: boolean;
  scriptLoaded: boolean;
  msSinceScriptAppended: number | null;
}

/**
 * ACDEV-27224: everything about the Forter fraud-token funnel that exists
 * SOLELY to answer "what happened, and when" lives here — the RUM calls, the
 * script-append timestamp, and the `scriptLoaded` flag. None of that is
 * needed for `forterService` to actually load the script and store the
 * token, so `forterService` only feeds this instance events (script
 * appended/loaded/failed, token arrived) and asks it for `isInitialized`
 * (which IS genuine service state, not a diagnostic) when a payment reads
 * the token.
 *
 * Every public method is self-guarded via `safe()` — a Coralogix hiccup must
 * never be able to affect script loading or a payment. A class rather than
 * free functions so this state is scoped to one `forterService` lifetime
 * instead of a module-level `let`, and so this can be subclassed/extended
 * (e.g. per-publisher instrumentation) without touching the funnel's call
 * sites.
 */
export class ForterDiagnostics {
  private hasLoggedTokenReceived = false;
  private scriptAppendedAt: number | null = null;
  private hasScriptLoaded = false;
  // Set on a `forterTokenMissingAtPayTime`, cleared once `tokenReceived()`
  // reports the resulting late arrival — see `tokenReadAtPayTime`.
  private hasLoggedMissingToken = false;
  private stallWatchdogTimer: ReturnType<typeof setTimeout> | null = null;

  private safe(fn: () => void): void {
    try {
      fn();
    } catch {
      /* diagnostics must never break the Forter flow or a payment */
    }
  }

  private msSinceScriptAppended(): number | null {
    return this.scriptAppendedAt ? Date.now() - this.scriptAppendedAt : null;
  }

  private clearStallWatchdog(): void {
    if (this.stallWatchdogTimer !== null) {
      clearTimeout(this.stallWatchdogTimer);
      this.stallWatchdogTimer = null;
    }
  }

  notConfigured(): void {
    this.safe(() => logWarn(InfoLogs.forterNotConfigured));
  }

  initializeFailed(error: unknown): void {
    this.safe(() => logError('Failed to initialize Forter', error));
  }

  /**
   * Call exactly when the `<script>` tag is appended — starts the funnel
   * clock AND arms the stall watchdog: if neither `scriptLoaded()` nor
   * `scriptLoadFailed()` lands within `SCRIPT_STALL_WATCHDOG_MS`, self-fires
   * `scriptLoadStalled()`. Covers the blocker that hangs the request
   * indefinitely instead of erroring it — otherwise indistinguishable from
   * "still loading" forever.
   */
  scriptLoadStarted(): void {
    this.scriptAppendedAt = Date.now();
    this.safe(() => logInfo(InfoLogs.forterScriptLoadStarted));
    this.clearStallWatchdog();
    this.stallWatchdogTimer = setTimeout(() => this.scriptLoadStalled(), SCRIPT_STALL_WATCHDOG_MS);
  }

  scriptLoaded(): void {
    this.clearStallWatchdog();
    this.hasScriptLoaded = true;
    this.safe(() => logInfo(InfoLogs.forterScriptLoaded, { loadDurationMs: this.msSinceScriptAppended() }));
  }

  scriptLoadFailed(): void {
    this.clearStallWatchdog();
    this.safe(() =>
      logError(ErrorLogs.forterScriptLoadFailed, undefined, { loadDurationMs: this.msSinceScriptAppended() })
    );
  }

  /** Self-fired by the watchdog armed in `scriptLoadStarted()` — not meant to be called directly. */
  private scriptLoadStalled(): void {
    this.stallWatchdogTimer = null;
    this.safe(() => logWarn(InfoLogs.forterScriptLoadStalled, { msSinceScriptAppended: this.msSinceScriptAppended() }));
  }

  /**
   * Fires at most once per `forterService` lifetime — see `reset()`. Also
   * reports a late arrival (`forterTokenArrivedAfterMiss`) if a payment
   * already went out with `forterTokenMissingAtPayTime` earlier THIS
   * session — proof this session would have shipped a real cookie had pay
   * happened even slightly later.
   */
  tokenReceived(): void {
    if (this.hasLoggedTokenReceived) return;
    this.hasLoggedTokenReceived = true;
    const msSinceScriptAppended = this.msSinceScriptAppended();
    this.safe(() => logInfo(InfoLogs.forterTokenReceived, { msSinceScriptAppended }));
    if (this.hasLoggedMissingToken) {
      this.hasLoggedMissingToken = false;
      this.safe(() => logInfo(InfoLogs.forterTokenArrivedAfterMiss, { msSinceScriptAppended }));
    }
  }

  /**
   * `isInitialized` is the one piece of this snapshot `forterService` — not
   * this class — actually owns, so it's passed in rather than tracked here.
   */
  getSnapshot(isInitialized: boolean): ForterFunnelSnapshot {
    return { isInitialized, scriptLoaded: this.hasScriptLoaded, msSinceScriptAppended: this.msSinceScriptAppended() };
  }

  /**
   * Fires once per payment from `getForterToken()` — the literal moment the
   * value shipped to the BE as `forterTokenCookie` is read. `getIsInitialized`
   * is a thunk rather than a precomputed value so a throwing
   * `forterService.isInitialized()` is caught by `safe()` too — callers need
   * no try/catch of their own.
   */
  tokenReadAtPayTime(source: ForterTokenSource, getIsInitialized: () => boolean): void {
    this.safe(() => {
      const snapshot = this.getSnapshot(getIsInitialized());
      if (source === 'none') {
        this.hasLoggedMissingToken = true;
        logWarn(InfoLogs.forterTokenMissingAtPayTime, { ...snapshot });
      } else {
        logInfo(InfoLogs.forterTokenPresentAtPayTime, { source, ...snapshot });
      }
    });
  }

  /**
   * Fires from `usePaymentToServer` — the statement immediately before
   * `apiClient.post()` hands the body to `JSON.stringify` + `fetch()`.
   * `requestParams` is the exact object about to be posted (the
   * `enrichWithForter` output); this method does its own defensive
   * shape-reading inside `safe()` so callers don't need to know or trust
   * the shape themselves. Proves the FE asked the browser to send a
   * non-empty token on THIS request — NOT that the BE received it (see
   * `tokenReadAtPayTime` for that distinction). Deliberately not called
   * from `usePciPaymentToServer` — that request goes to the PCI gateway
   * and its body is kept out of any FE-side inspection.
   *
   * `paymentMethodId` is the top-level field every payment builder sets
   * (see e.g. `card.ts`, `stripe.ts`, `paypal.ts`) — an internal appcharge
   * payment-method-config id, not a PSP token or any customer PII, so it's
   * safe to ship and gives a per-request join key without touching the
   * rest of `params` (which carries real PII: email, address, card
   * metadata). `orderId` is deliberately NOT read/logged here — it's never
   * part of the request body; it's already a global Coralogix RUM label
   * (see `setCoralogixLabels({ order_id })` in `CheckoutContent`) that
   * auto-attaches to every event this SDK ships, this one included.
   *
   * `forterTokenCookie` ships the raw token value itself — it's Forter's
   * own fraud-detection token, not a payment credential, and the exact
   * same value is already sent to the BE on this same request, so this
   * adds no exposure beyond what the network call already carries. It's
   * what lets a value logged here be diffed byte-for-byte against what
   * the BE actually received.
   */
  tokenHandedToFetch(endpoint: string, requestParams: unknown): void {
    this.safe(() => {
      const outerParams = requestParams as Record<string, unknown> | undefined;
      const innerParams = outerParams?.params as Record<string, unknown> | undefined;
      const fraudDetection = innerParams?.fraudDetection as Record<string, unknown> | undefined;
      const forterTokenCookie =
        typeof fraudDetection?.forterTokenCookie === 'string' ? fraudDetection.forterTokenCookie : '';

      logInfo(InfoLogs.forterTokenHandedToFetch, {
        endpoint,
        tokenPresent: forterTokenCookie !== '',
        tokenLength: forterTokenCookie.length,
        forterTokenCookie,
        paymentMethodId: typeof outerParams?.paymentMethodId === 'string' ? outerParams.paymentMethodId : null,
      });
    });
  }

  /** Call from `forterService.cleanup()` so a re-initialized session starts its funnel timings and one-shot guards fresh. */
  reset(): void {
    this.clearStallWatchdog();
    this.hasLoggedTokenReceived = false;
    this.scriptAppendedAt = null;
    this.hasScriptLoaded = false;
    this.hasLoggedMissingToken = false;
  }
}

export const forterDiagnostics = new ForterDiagnostics();
